import axios from "axios";

// If REACT_APP_BACKEND_URL is empty/unset, fall back to a relative `/api`
// path so requests go through the Netlify proxy. That keeps the browser
// origin the same as the frontend, which is required for Safari (mobile
// and desktop) to send auth cookies on cross-site requests.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ---------- Products ----------
export const fetchProducts = async (params = {}) => {
  const { data } = await api.get("/products", { params });
  return data;
};

export const fetchProduct = async (id) => {
  const { data } = await api.get(`/products/${id}`);
  return data;
};

export const createProduct = async (payload) => {
  const { data } = await api.post("/products", payload);
  return data;
};

export const updateProduct = async (id, payload) => {
  const { data } = await api.put(`/products/${id}`, payload);
  return data;
};

export const deleteProduct = async (id) => {
  const { data } = await api.delete(`/products/${id}`);
  return data;
};

export const bulkUpdateProducts = async (payload) => {
  const { data } = await api.post("/products/bulk-update", payload);
  return data;
};

// ---------- Settings ----------
export const fetchSettings = async () => {
  const { data } = await api.get("/settings");
  return data;
};

export const updateSettings = async (payload) => {
  const { data } = await api.put("/settings", payload);
  return data;
};

// ---------- Global Terms ----------
// kind: "prebook" | "in_stock" (default "prebook" on backend)
export const getTerms = async (kind = "prebook") => {
  const { data } = await api.get("/settings/terms", { params: { kind } });
  return data; // { kind, text, version, updated_at }
};

export const getAllTerms = async () => {
  const { data } = await api.get("/settings/terms/all");
  return data; // { prebook: {...}, in_stock: {...} }
};

export const updateTerms = async (kind, payload) => {
  // payload: { text, force_resign }
  const { data } = await api.put("/settings/terms", payload, { params: { kind } });
  return data;
};

// ---------- Auth ----------
export const apiRegister = async (payload) => {
  const { data } = await api.post("/auth/register", payload);
  return data;
};

export const apiLogin = async (payload) => {
  const { data } = await api.post("/auth/login", payload);
  return data;
};

export const apiLogout = async () => {
  const { data } = await api.post("/auth/logout");
  return data;
};

export const apiMe = async () => {
  const { data } = await api.get("/auth/me");
  return data;
};

export const apiUpdateMyProvince = async (province) => {
  const { data } = await api.put("/auth/me/province", { province });
  return data;
};

export const apiUpdateMyProfile = async (payload) => {
  const { data } = await api.patch("/auth/me", payload);
  return data;
};

export const apiForgotPassword = async (email) => {
  const { data } = await api.post("/auth/forgot-password", { email });
  return data;
};

export const apiResetPassword = async (token, newPassword) => {
  const { data } = await api.post("/auth/reset-password", { token, new_password: newPassword });
  return data;
};

// ---------- Users (admin) ----------
export const fetchUsers = async () => {
  const { data } = await api.get("/users");
  return data;
};

export const setUserApproval = async (id, isApproved) => {
  const { data } = await api.patch(`/users/${id}/approval`, { is_approved: isApproved });
  return data;
};

export const deleteUser = async (id) => {
  const { data } = await api.delete(`/users/${id}`);
  return data;
};

// ---------- Orders ----------
export const createOrder = async (payload) => {
  const { data } = await api.post("/orders", payload);
  return data;
};

export const fetchOrders = async () => {
  const { data } = await api.get("/orders");
  return data;
};

export const fetchOrder = async (id) => {
  const { data } = await api.get(`/orders/${id}`);
  return data;
};

export const updateOrderStatus = async (id, status) => {
  const { data } = await api.patch(`/orders/${id}/status`, { status });
  return data;
};

export const deleteOrder = async (id) => {
  await api.delete(`/orders/${id}`);
};

export const updateOrderInvoice = async (id, payload) => {
  // payload: { items?, shipping_cost?, discount_pct? }
  const { data } = await api.patch(`/admin/orders/${id}/invoice`, payload);
  return data;
};

export const sendPaymentReminder = async (id) => {
  const { data } = await api.post(`/admin/orders/${id}/send-reminder`);
  return data;
};

export const updateOrderTracking = async (id, { carrier, number, url }) => {
  const { data } = await api.post(`/admin/orders/${id}/tracking`, { carrier, number, url });
  return data;
};

// ---------- Admin uploads ----------
export const uploadProductImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/uploads/image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { url, filename, size }
};

export const uploadCatalogPdf = async (file, type) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/admin/uploads/catalog?type=${type}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const fetchCatalogStatus = async () => {
  const { data } = await api.get("/admin/uploads/catalogs/status");
  return data;
};

export const deleteCatalogPdf = async (type) => {
  const { data } = await api.delete(`/admin/uploads/catalog/${type}`);
  return data;
};

// Build a full URL for a relative /uploads/... path returned from the API
export const uploadUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

// ---------- Homepage gallery ----------
export const fetchHomeImages = async () => {
  const { data } = await api.get("/home-images");
  return data;
};

export const addHomeImage = async ({ url, alt, product_id }) => {
  const { data } = await api.post("/admin/home-images", { url, alt: alt || "", product_id: product_id || null });
  return data;
};

export const updateHomeImage = async (id, { product_id, alt } = {}) => {
  const payload = {};
  if (product_id !== undefined) payload.product_id = product_id ?? "";
  if (alt !== undefined) payload.alt = alt;
  const { data } = await api.patch(`/admin/home-images/${id}`, payload);
  return data;
};

export const deleteHomeImage = async (id) => {
  const { data } = await api.delete(`/admin/home-images/${id}`);
  return data;
};

// ---------- Admin email templates ----------
export const fetchEmailTemplates = async () => {
  const { data } = await api.get("/admin/email-templates");
  return data;
};

export const updateEmailTemplate = async (key, payload) => {
  const { data } = await api.put(`/admin/email-templates/${key}`, payload);
  return data;
};

export const resetEmailTemplate = async (key) => {
  const { data } = await api.post(`/admin/email-templates/${key}/reset`);
  return data;
};

export const previewEmailTemplate = async (key) => {
  const { data } = await api.post(`/admin/email-templates/${key}/preview`);
  return data; // { subject, html }
};

export const sendTestEmail = async (key, to) => {
  const { data } = await api.post(`/admin/email-templates/${key}/test-send`, { to });
  return data;
};

